import { Link } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";
import { track } from "../lib/api";

/* Shared £100 Homebuyer Cashback promotion surfaces.
   Visibility-only nudges — calm, secondary, on-brand (green/teal).
   No pop-ups, timers, sticky banners or "limited time" language.
   Terminology is always "claim £100 cashback" (never "£100 off" etc.). */

const QUALIFYING = "England & Wales · 18+ · One payment per property purchase · Claim within 90 days of completion · ";

/* Compact promotional band — homepage, directly below the hero. */
export const CashbackBanner = ({ source = "homepage" }) => (
  <section className="mp-container py-8 sm:py-10" data-testid="cashback-home-banner">
    <div className="rounded-2xl border border-[#0F766E]/20 bg-gradient-to-br from-[#0F766E]/6 via-white to-[#10B981]/8 p-6 sm:p-8">
      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <span className="w-12 h-12 rounded-xl bg-[#0F766E]/10 border border-[#0F766E]/20 grid place-items-center shrink-0" aria-hidden="true">
            <Gift className="w-6 h-6 text-[#0F766E]" />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-[#0F766E]">£100 Homebuyer Cashback</div>
            <h2 className="font-sora text-xl sm:text-2xl font-semibold text-[#0B1220] mt-1">
              Buying a home? Get <span className="text-[#0B5F58]">£100 cashback</span>
            </h2>
            <p className="mt-2 text-sm text-[#475569] leading-relaxed max-w-2xl">
              Use all three qualifying MortgagePath routes for your mortgage, conveyancing and property survey, and
              claim £100 cashback when your purchase completes.
            </p>
          </div>
        </div>
        <Link
          to="/cashback"
          onClick={() => track("cashback_prompt_click", { source, cta_location: source })}
          className="mp-btn-primary shrink-0 whitespace-nowrap self-start md:self-auto"
          data-testid="cashback-home-cta"
        >
          How the £100 cashback works <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <p className="mt-4 text-xs text-[#94A3B8] leading-relaxed" data-testid="cashback-home-qualifying">
        {QUALIFYING}
        <Link to="/cashback#terms" className="underline hover:text-[#0F766E]" data-testid="cashback-home-terms">
          T&amp;Cs apply
        </Link>
        .
      </p>
    </div>
  </section>
);

/* Secondary, restrained prompt — high-intent tool / result / guide pages. */
export const CashbackPromptCard = ({ source, className = "" }) => (
  <Link
    to="/cashback"
    onClick={() => track("cashback_prompt_click", { source, cta_location: source })}
    data-testid={`cashback-prompt-${source}`}
    className={`block rounded-2xl border border-[#DCE7E3] bg-gradient-to-br from-[#0F766E]/4 via-white to-[#10B981]/5 hover:border-[#0F766E]/40 transition-all p-5 group text-left ${className}`}
  >
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#0F766E]/8 border border-[#0F766E]/15 grid place-items-center shrink-0" aria-hidden="true">
        <Gift className="w-5 h-5 text-[#0F766E]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-[#0F766E]">£100 Homebuyer Cashback</div>
        <h3 className="font-sora text-base sm:text-lg font-semibold text-[#0B1220] mt-0.5 group-hover:text-[#0F766E] transition-colors">
          Buying this home? You could claim £100 cashback
        </h3>
        <p className="mt-1 text-sm text-[#475569] leading-relaxed">
          Use MortgagePath for your qualifying mortgage, conveyancing and property survey routes and claim £100 cashback
          when your purchase completes.
        </p>
        <span className="mt-2.5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F766E]">
          See how cashback works <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
        </span>
      </div>
    </div>
  </Link>
);

export default CashbackPromptCard;
